import React, { useCallback, useEffect, useState } from 'react';

import {
    StyleProp,
    Text,
    TextStyle,
    TouchableOpacity,
    View,
    ViewStyle,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import TextTruncate from 'react-text-truncate';

import { useFocusEffect } from '@react-navigation/native';

import { Fonts } from 'assets/fonts';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    DETAIL_DESCRIPTION_ID,
    DETAIL_DESCRIPTION_MORE_BUTTON_ID,
} from 'config/testID';

import isFunction from 'utils/function';
import { isWeb } from 'utils/responsiveness';

import { MORE_LABEL } from 'components/DescriptionMore/languageConst';
import LocalizedText from 'components/LocalizedText';

import styles from './styles';

type Props = {
    onPress: () => void;
    description?: string | null;
    textStyle?: StyleProp<TextStyle>;
    containerStyle?: StyleProp<ViewStyle>;
    isBtnShowed?: boolean;
    screenReaderEnabled?: boolean;
    numberOfLines?: number;
    onDescriptionLoaded?: () => void;
};

const LENGTH_DOTS = 4;

const formattedData = (lines: any = [], numberOfLines: number) => {
    const linesLength = lines.reduce(
        (acc: any, current: { text: string }) =>
            acc + current.text.trimEnd().length,
        0,
    );

    const showLines = lines.reduce(
        (acc: any, current: { text: string }, index: number) =>
            index < numberOfLines ? acc + current.text.trimEnd().length : acc,
        0,
    );

    return {
        linesLength,
        showLines,
    };
};

const {
    ACCESSIBILITY: {
        DETAIL_PAGE: { ACCESSIBILITY_DETAIL_PAGE_MORE_OPTION },
    },
} = LANGUAGE_KEYS;

const DescriptionMore = ({
    description,
    onPress,
    textStyle,
    containerStyle,
    numberOfLines = 3,
    isBtnShowed = true,
    onDescriptionLoaded,
    screenReaderEnabled,
}: Props) => {
    const { t } = useTranslation();
    const moreTextLength = t(MORE_LABEL).length;
    const [showMore, setShowMore] = useState(false);
    const [isWebTruncated, setIsWebTruncated] = useState(false);
    const [sliceLength, setSlice] = useState(0);
    const [webDescriptionKey, setWebDescriptionKey] = useState(Date.now());
    const truncateText = `... ${t(MORE_LABEL)}`;

    const onTextLayout = useCallback(
        (e) => {
            const { lines } = e.nativeEvent;

            if (
                lines &&
                Array.isArray(lines) &&
                lines.length >= numberOfLines
            ) {
                const { linesLength, showLines } = formattedData(
                    lines,
                    numberOfLines,
                );

                if (linesLength > showLines) {
                    const line = showLines - (LENGTH_DOTS + moreTextLength);
                    setSlice(line);
                    setShowMore(true);
                }
            }

            if (onDescriptionLoaded && isFunction(onDescriptionLoaded)) {
                onDescriptionLoaded();
            }
        },
        [numberOfLines, onDescriptionLoaded, moreTextLength],
    );

    const modifyStyle = useCallback(() => {
        if (isWeb) {
            const moreText = document.getElementById('more-text');
            if (moreText) {
                setIsWebTruncated(true);

                moreText.innerHTML = '';

                const dotsSpan = document.createElement('span');
                dotsSpan.textContent = '... ';
                dotsSpan.style.fontFamily = Fonts.main.regular;

                const moreSpan = document.createElement('span');
                moreSpan.textContent = t(MORE_LABEL);
                moreSpan.style.color = 'white';
                moreSpan.style.fontFamily = Fonts.main.bold;

                moreText.append(dotsSpan, moreSpan);

                moreText.style.cursor = 'pointer';
                moreText.style.textTransform = 'uppercase';
                moreText.onclick = onPress;
                moreText.setAttribute(
                    'data-testid',
                    'DETAIL_DESCRIPTION_MORE_BUTTON_ID',
                );
            }
        }
    }, [onPress]);

    const handleResize = useCallback(() => {
        setWebDescriptionKey(Date.now());
    }, []);

    useEffect(() => {
        if (isWeb) {
            window.addEventListener('resize', handleResize);
            modifyStyle();
        }

        return () => {
            if (isWeb) {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, []);

    useFocusEffect(
        useCallback(() => {
            setWebDescriptionKey(Date.now());
            modifyStyle();

            return () => {
                setWebDescriptionKey(Date.now());
            };
        }, [modifyStyle]),
    );

    if (!description) {
        return null;
    }

    return (
        <View style={[styles.moreContainer, containerStyle]}>
            <TouchableOpacity
                testID={DETAIL_DESCRIPTION_MORE_BUTTON_ID}
                onPress={onPress}
                disabled={isWeb || !(screenReaderEnabled || isBtnShowed)}
                accessibilityLabel={t(ACCESSIBILITY_DETAIL_PAGE_MORE_OPTION)}
                accessibilityRole="button"
                // @ts-ignore
                tabIndex={0}
            >
                {isWeb ? (
                    <View
                        style={styles.webContainer}
                        onLayout={modifyStyle}
                        key={webDescriptionKey}
                    >
                        <TextTruncate
                            line={numberOfLines}
                            element="div"
                            truncateText={truncateText}
                            text={
                                isWebTruncated
                                    ? description
                                    : description + truncateText.toUpperCase()
                            }
                        />
                    </View>
                ) : (
                    <Text
                        testID={DETAIL_DESCRIPTION_ID}
                        style={[styles.moreTextView, textStyle]}
                        onTextLayout={onTextLayout}
                    >
                        {showMore
                            ? `${description.substring(0, sliceLength)}... `
                            : `${description}... `}
                        <LocalizedText
                            textKey={MORE_LABEL}
                            style={styles.moreTextButton}
                            onPress={onPress}
                        />
                    </Text>
                )}
            </TouchableOpacity>
        </View>
    );
};

export default DescriptionMore;
